import { batch } from 'editor/src/store/batchedSubscribeEnhancer';
import { removeConditionOptionAction } from 'editor/src/store/design/slice';
import { ConditionAddress } from 'editor/src/store/design/types';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

import getConditionChildrenKey from '../reducer/getConditionChildrenKey';

import applyTextVariantsForConditionOperation from './applyTextVariantsForConditionOperation';
import { removeElementFromReflowGroupsOperation } from './reflowGroupOperations';
import updateHiddenElementsOperation from './updateHiddenElementsOperation';

const removeConditionOptionOperation =
  (address: ConditionAddress, optionId: string) => (dispatch: ThunkDispatch, getState: () => RootState) => {
    // Capture the option's linked element uuids before removal so they can be
    // dropped from any reflow row, keeping rows in sync with the condition.
    const conditionGroup = getState().design.designData?.spreads[address.spreadIndex]?.conditionGroup;
    const removedUuids =
      conditionGroup?.children[getConditionChildrenKey(address.conditionId, optionId)]
        ?.filter((child) => child.type !== 'condition')
        .map((child) => child.id) ?? [];
    // Removing the ACTIVE option moves the condition onto another one (see
    // removeConditionOptionReducer), so any text varying with it has to follow
    // — otherwise the labels keep showing the removed option's language.
    const removedActiveOption = conditionGroup?.conditions[address.conditionId]?.activeOptionId === optionId;

    batch(() => {
      dispatch(removeConditionOptionAction({ address, optionId }));

      const activeOptionId = removedActiveOption
        ? getState().design.designData?.spreads[address.spreadIndex]?.conditionGroup?.conditions[address.conditionId]
            ?.activeOptionId
        : undefined;
      // Still pointing at the option we asked to remove means the reducer
      // refused it (it keeps a condition at two options), so nothing moved.
      const recomputed =
        activeOptionId && activeOptionId !== optionId
          ? dispatch(applyTextVariantsForConditionOperation(address.conditionId, activeOptionId))
          : new Set<number>();

      // The condition's own spread, not the viewer's: a design-scoped picklist
      // (showOnAllSpreads) is edited from pages that do not store it. Skipped
      // when the variant swap already re-packed it.
      if (!recomputed.has(address.spreadIndex)) {
        dispatch(updateHiddenElementsOperation(address.spreadIndex));
      }
      removedUuids.forEach((uuid) => dispatch(removeElementFromReflowGroupsOperation(address.spreadIndex, uuid)));
    });
  };

export default removeConditionOptionOperation;
